import { useCallback, useEffect, useState } from 'react';
import { pdApi, type PdScoringBand } from '../api';
import './RecommendationScoring.css';
import { HelpPanel } from '../HelpPanel';
import { RECOMMENDATION } from '../helpContent';

/**
 * Settings → Assessment Framework → Recommendation ("Recommendation Scoring").
 *
 * CLONED from `/Users/glucic/src/ai_readiness_template/views/setup-scoring.ejs` and its
 * edit page `views/setup-scoring-form.ejs`.
 *
 * WHAT THIS SCALE DOES: it turns a numeric assessment result (1.00–5.00) into a named
 * recommendation and a colour. Every scored opportunity lands in exactly one band, and the
 * colour is reused on dashboards and reports — which is why the card leads with a colour
 * swatch rather than treating it as an afterthought.
 *
 * EDITING IS A SEPARATE SCREEN, not a dialog. The source navigates to
 * `/setup/scoring/:id/edit` and back; this swaps the body for the same form rather than
 * opening a modal over the list, because a modal would be a different screen from the one
 * that was designed.
 *
 * EDIT ONLY — no create, no delete. The bands are configuration of an existing scale, and
 * adding or removing one would leave a score range that resolves to nothing.
 */

export function RecommendationScoring() {
  const [bands, setBands] = useState<PdScoringBand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PdScoringBand | null>(null);
  /* Measured — CLAUDE.md, "A screen header never scrolls". */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.settings.scoring();
      setBands(data.scoring_recommendations);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!editing) return;
    setError(null);
    try {
      await pdApi.settings.updateScoringBand(editing.id, editing);
      setEditing(null);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const addBand = async () => {
    setError(null);
    try {
      await pdApi.settings.createScoringBand({});
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const removeBand = async (b: PdScoringBand) => {
    if (!window.confirm(`Remove the \u201C${b.label}\u201D threshold?`)) return;
    setError(null);
    try {
      await pdApi.settings.deleteScoringBand(b.id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  /* ---- the edit screen ---- */
  useEffect(() => {
  if (!rootEl) return;
  const main = rootEl.closest('main');
  const measure = () => {
    const top = rootEl.getBoundingClientRect().top;
    const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
    const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
    setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
  };
  measure();
  window.addEventListener('resize', measure);
  const ro = new ResizeObserver(measure);
  if (main) ro.observe(main);
  return () => {
    window.removeEventListener('resize', measure);
    ro.disconnect();
  };
  }, [rootEl]);

  if (editing) {
  return (
      <div className="rs-page">
        <div className="rs-page-header">
          <div>
            <h1 className="rs-page-header__title">Edit Score Range</h1>
          </div>
        </div>

        {error ? <div className="rs-error">{error}</div> : null}

        <div className="rs-form-card">
          <div className="rs-form-row">
            <div className="rs-input-field">
              <input
                id="min_score"
                type="number"
                step="0.01"
                value={editing.min_score}
                onChange={(e) => setEditing({ ...editing, min_score: Number(e.target.value) })}
              />
              <label htmlFor="min_score">Minimum Score *</label>
            </div>
            <div className="rs-input-field">
              <input
                id="max_score"
                type="number"
                step="0.01"
                value={editing.max_score}
                onChange={(e) => setEditing({ ...editing, max_score: Number(e.target.value) })}
              />
              <label htmlFor="max_score">Maximum Score *</label>
            </div>
          </div>

          <div className="rs-input-field">
            <input
              id="label"
              type="text"
              value={editing.label}
              onChange={(e) => setEditing({ ...editing, label: e.target.value })}
            />
            <label htmlFor="label">Level Name *</label>
          </div>

          <div className="rs-input-field">
            <textarea
              id="description"
              value={editing.description ?? ''}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
            />
            <label htmlFor="description">Description (Optional)</label>
          </div>

          <div className="rs-input-field">
            <textarea
              id="recommendations"
              style={{ minHeight: '10rem' }}
              value={editing.recommendations ?? ''}
              onChange={(e) => setEditing({ ...editing, recommendations: e.target.value })}
            />
            <label htmlFor="recommendations">Recommendation Text *</label>
          </div>

          <div className="rs-input-field">
            <input
              id="color_code"
              type="text"
              value={editing.color_code ?? ''}
              onChange={(e) => setEditing({ ...editing, color_code: e.target.value })}
            />
            <label htmlFor="color_code">Color Code *</label>
          </div>

          <div className="rs-form-actions">
            <button type="button" className="rs-btn rs-btn-blue" onClick={save}>
              Save Changes
            </button>
            <button
              type="button"
              className="rs-btn rs-btn-grey"
              onClick={() => {
                setEditing(null);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ---- the list ---- */
  return (
    <div className="rs-page" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <div className="rs-page-header">
        <div>
          <h1 className="rs-page-header__title">Recommendation Scoring</h1>
          <p className="rs-page-header__subtitle">
            Configure scoring thresholds and recommendations based on assessment results
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={RECOMMENDATION} />
        </div>
      </div>

      {error ? <div className="rs-error">{error}</div> : null}

      <div className="rs-thresholds-container">
        <div className="rs-section-bar">
          <h3>Readiness Scoring Thresholds</h3>
          {/* Add and remove, which the source never offered — deleting a band used to be
              permanent. A new band takes the range above the current top, up to 5. */}
          <button
            type="button"
            className="rs-btn-sm rs-btn-outline-primary"
            onClick={() => void addBand()}
          >
            + Add threshold
          </button>
        </div>
        <div className="rs-thresholds-list rs-thresholds-list--scroll">
          {loading ? (
            <div className="rs-empty-state">
              <p>Loading…</p>
            </div>
          ) : bands.length === 0 ? (
            <div className="rs-empty-state">
              <p>No scoring thresholds found. Please contact your administrator.</p>
            </div>
          ) : (
            bands.map((b) => (
              <div key={b.id} className="rs-threshold-card">
                <div className="rs-threshold-body">
                  <div className="rs-threshold-header">
                    <div>
                      <h4>{b.label}</h4>
                      <span
                        className="rs-score-range"
                        style={{ color: b.color_code ?? '#1976d2' }}
                      >
                        {Number(b.min_score).toFixed(2)} – {Number(b.max_score).toFixed(2)}
                      </span>
                    </div>
                    <div className="rs-card-actions">
                      <button
                        type="button"
                        className="rs-btn-sm rs-btn-outline-primary"
                        title="Edit threshold"
                        onClick={() => setEditing({ ...b })}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="rs-btn-sm rs-btn-outline-danger"
                        title="Remove threshold"
                        onClick={() => void removeBand(b)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <p className="rs-threshold-description">{b.description}</p>

                  {/*
                    The recommendations are stored as newline-separated bullets ("• Pause
                    initiative for…"). Rendering them in one `<p>` ran them into a wall of
                    text; each line is its own item now, which is how they were written.
                  */}
                  {b.recommendations ? (
                    <div className="rs-threshold-recommendations">
                      <span className="rs-rec-label">Recommended actions</span>
                      <ul>
                        {String(b.recommendations)
                          .split('\n')
                          .map((line) => line.replace(/^[•\-\s]+/, '').trim())
                          .filter(Boolean)
                          .map((line, i) => (
                            <li key={i}>{line}</li>
                          ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
